import { CommunicationChannel } from '../../types/communications';
import { getFilterRules, type Filters } from '../../types/filter';
import {
    ActionType,
    isJourneyFilterGroup,
    isJourneyFilterRule,
    type JourneyCreatePayload,
    type JourneyFilterGroup,
    type JourneyFiltersConfig,
} from '../../types/journeys';
import type {
    JourneyNodeNameDescriptionResponse,
    JourneyNodeNamingPatch,
} from '../../types/llm';
import {
    MAX_JOURNEY_NAMING_NODE_SIGNALS,
    MAX_NAMING_SIBLING_NODE_NAMES,
} from '../../types/llm';
import {
    getNestedMetricQueries,
    isNestedMetricQuery,
    type NestedMetricQuery,
    type NestedMetricQueryGroup,
} from '../../types/metricQuery';
import { Timezones } from '../../types/timezone';
import {
    isJourneyNodeDescriptionDefault,
    isJourneyNodeTitleDefault,
} from '../journeys';
import { parseReservedFilterValue } from './filters';
import {
    CHANNEL_LABELS,
    sanitizeHumanText,
    stripUuidLikeTokens,
    truncateAtWordBoundary,
} from './humanize';
import {
    getEntrySummary,
    getNodeActionSignals,
    getNodeKindTitle,
    getNodeNamingSignals,
} from './signals';
import type {
    JourneyCompositionSummary,
    JourneyEntityRefs,
    JourneyNameDescriptionContext,
    JourneyNamingHydratedEntities,
    JourneyNodeNameDescriptionContext,
} from './types';

const collectAudienceIdsFromFilterValues = (
    values: unknown[] | undefined,
    audienceIds: Set<string>,
): void => {
    for (const value of values ?? []) {
        const parsed = parseReservedFilterValue(value);
        if (parsed?.kind === 'audience') {
            audienceIds.add(parsed.id);
        }
    }
};

const collectAudienceIdsFromFilters = (
    filters: Filters | undefined,
    audienceIds: Set<string>,
): void => {
    if (!filters) return;
    for (const rule of getFilterRules(filters)) {
        collectAudienceIdsFromFilterValues(rule.values, audienceIds);
    }
};

const collectAudienceIdsFromNestedMetricQuery = (
    query: NestedMetricQuery,
    audienceIds: Set<string>,
): void => {
    if (isNestedMetricQuery(query)) {
        for (const child of getNestedMetricQueries(query)) {
            collectAudienceIdsFromNestedMetricQuery(child, audienceIds);
        }
        return;
    }
    collectAudienceIdsFromFilters(query.filters, audienceIds);
};

const collectAudienceIdsFromJourneyFilterGroup = (
    group: JourneyFilterGroup | undefined,
    audienceIds: Set<string>,
): void => {
    if (!group) return;
    const items = 'and' in group ? group.and : group.or;
    for (const item of items) {
        if (isJourneyFilterGroup(item)) {
            collectAudienceIdsFromJourneyFilterGroup(item, audienceIds);
        } else if (isJourneyFilterRule(item)) {
            collectAudienceIdsFromFilterValues(item.values, audienceIds);
        }
    }
};

const collectAudienceIdsFromFilterConfig = (
    filterConfig: JourneyFiltersConfig | undefined,
    audienceIds: Set<string>,
): void => {
    if (!filterConfig) return;

    const compiledId = filterConfig.audienceFilters?.compiledAudienceId;
    if (compiledId) audienceIds.add(compiledId);

    const audienceFilterConfig = filterConfig.audienceFilters?.filterConfig as
        | NestedMetricQueryGroup
        | undefined;
    if (audienceFilterConfig) {
        collectAudienceIdsFromNestedMetricQuery(
            audienceFilterConfig,
            audienceIds,
        );
    }

    collectAudienceIdsFromJourneyFilterGroup(
        filterConfig.journeyFilters,
        audienceIds,
    );
};

export const collectJourneyEntityRefs = (
    journey: JourneyCreatePayload,
    options?: { nodeIds?: string[] },
): JourneyEntityRefs => {
    const allNodes = journey.config?.nodes ?? [];
    const nodes =
        options?.nodeIds !== undefined
            ? allNodes.filter((n) => options.nodeIds!.includes(n.id))
            : allNodes;

    const audienceIds = new Set<string>();
    const templateIds = new Set<string>();
    const destinationJourneyIds = new Set<string>();

    for (const node of nodes) {
        for (const action of node.actions ?? []) {
            switch (action.type) {
                case ActionType.FILTER: {
                    collectAudienceIdsFromFilterConfig(
                        action.config.filterConfig,
                        audienceIds,
                    );
                    break;
                }
                case ActionType.WAIT_UNTIL: {
                    collectAudienceIdsFromFilterConfig(
                        action.config.eventConfig?.filterConfig,
                        audienceIds,
                    );
                    break;
                }
                case ActionType.SEND_MESSAGE: {
                    const { messageConfig } = action.config;
                    const { communicationDetails, templateDetails } =
                        messageConfig;

                    const firstCommunication = communicationDetails
                        ? Object.values(communicationDetails)[0]
                        : undefined;
                    if (
                        firstCommunication?.templateId &&
                        typeof firstCommunication.templateId === 'string'
                    ) {
                        templateIds.add(firstCommunication.templateId);
                    }

                    const firstTemplate =
                        templateDetails &&
                        (Object.values(templateDetails)[0] as
                            | { id?: unknown }
                            | undefined);
                    const templateId = firstTemplate?.id;
                    if (typeof templateId === 'string') {
                        templateIds.add(templateId);
                    }
                    break;
                }
                case ActionType.SEND_TO_JOURNEY: {
                    for (const destinationId of action.config
                        .sendToJourneyConfig.destinationJourneyIds) {
                        destinationJourneyIds.add(destinationId);
                    }
                    break;
                }
                default:
                    break;
            }
        }

        const branches = node.branchConfig?.children?.branches ?? [];
        for (const branch of branches) {
            collectAudienceIdsFromFilterConfig(branch.conditions, audienceIds);
        }
    }

    return {
        audienceIds: Array.from(audienceIds),
        templateIds: Array.from(templateIds),
        destinationJourneyIds: Array.from(destinationJourneyIds),
    };
};

export const applyJourneyNodeNamingPatches = (
    journey: JourneyCreatePayload,
    patches: JourneyNodeNamingPatch[],
): JourneyCreatePayload => {
    if (patches.length === 0) return journey;

    const byNodeId = new Map(patches.map((patch) => [patch.nodeId, patch]));

    return {
        ...journey,
        config: {
            ...journey.config,
            nodes:
                journey.config?.nodes.map((node) => {
                    const patch = byNodeId.get(node.id);
                    if (!patch) return node;

                    const title = patch.title.trim() ? patch.title : node.title;

                    return {
                        ...node,
                        title,
                        description: patch.description.trim()
                            ? patch.description
                            : node.description,
                    };
                }) ?? [],
        },
    };
};

/** Sentence case, no trailing period — how a marketer types a label. */
const toNodeNameCasing = (value: string): string => {
    const withoutTrailingPeriod = value.replace(/\.+$/, '').trim();
    if (!withoutTrailingPeriod) return '';
    return (
        withoutTrailingPeriod.charAt(0).toUpperCase() +
        withoutTrailingPeriod.slice(1)
    );
};

/**
 * Sibling nodes must not share a name. Suffixing keeps the generated name —
 * blanking it would drop the node back to its "Untitled email" placeholder.
 */
const disambiguateNodeName = (
    nodeName: string,
    usedNodeNames: Set<string>,
    nameMax: number,
): string => {
    if (!usedNodeNames.has(nodeName.toLowerCase())) return nodeName;

    for (let suffix = 2; suffix <= 20; suffix += 1) {
        const candidate = truncateAtWordBoundary(
            `${nodeName} ${suffix}`,
            nameMax,
        );
        if (!usedNodeNames.has(candidate.toLowerCase())) return candidate;
    }
    return nodeName;
};

/**
 * Titles of the nodes we are *not* naming. Doubles as few-shot context for the
 * model and as the collision set the response is checked against.
 */
export const getSiblingNodeNames = (
    journey: JourneyCreatePayload,
    requestedNodeIds: string[],
): string[] =>
    (journey.config?.nodes ?? [])
        .filter((n) => !requestedNodeIds.includes(n.id))
        .filter((n) => !isJourneyNodeTitleDefault(n))
        .map((n) => n.title)
        .filter(Boolean);

export const normalizeJourneyNodeNameDescriptionResponse = (
    raw: JourneyNodeNameDescriptionResponse,
    requestedNodeIds: string[],
    options?: {
        nameMax?: number;
        descMax?: number;
        /** Names already in use elsewhere in the journey — never collide with these. */
        existingNames?: string[];
        /** Used when the model returns a blank name/description for a node. */
        fallbackByNodeId?: Record<
            string,
            { nodeName: string; nodeDescription: string }
        >;
        /**
         * When provided, LLM titles that are still default aliases are rejected
         * (treated as blank → fallback / empty).
         */
        isDefaultTitleAlias?: (nodeId: string, title: string) => boolean;
    },
): JourneyNodeNameDescriptionResponse => {
    const nameMax = options?.nameMax ?? 50;
    const descMax = options?.descMax ?? 100;
    const byNodeId = new Map(raw.nodes.map((n) => [n.nodeId, n]));
    const usedNodeNames = new Set(
        (options?.existingNames ?? []).map((name) => name.trim().toLowerCase()),
    );

    return {
        nodes: requestedNodeIds.map((nodeId) => {
            // Exact nodeId match only — never remap by index.
            const node = byNodeId.get(nodeId);
            const fallback = options?.fallbackByNodeId?.[nodeId];
            let rawName = (node?.nodeName ?? '').trim() || fallback?.nodeName;
            if (rawName && options?.isDefaultTitleAlias?.(nodeId, rawName)) {
                rawName = fallback?.nodeName;
            }
            const rawDescription =
                (node?.nodeDescription ?? '').trim() ||
                fallback?.nodeDescription ||
                '';

            let nodeName = rawName
                ? toNodeNameCasing(
                      truncateAtWordBoundary(
                          sanitizeHumanText(stripUuidLikeTokens(rawName)),
                          nameMax,
                      ),
                  )
                : '';
            const nodeDescription = rawDescription
                ? truncateAtWordBoundary(
                      sanitizeHumanText(stripUuidLikeTokens(rawDescription)),
                      descMax,
                  )
                : '';

            if (nodeName && options?.isDefaultTitleAlias?.(nodeId, nodeName)) {
                nodeName = '';
            }

            if (nodeName) {
                nodeName = disambiguateNodeName(
                    nodeName,
                    usedNodeNames,
                    nameMax,
                );
                usedNodeNames.add(nodeName.toLowerCase());
            }

            return { nodeId, nodeName, nodeDescription };
        }),
    };
};

export const buildJourneyNodeNameDescriptionContext = (
    journey: JourneyCreatePayload,
    hydratedEntities: JourneyNamingHydratedEntities,
    requestedNodeIds: string[],
    workspaceTimezone: string = Timezones.UTC,
): JourneyNodeNameDescriptionContext => {
    const nodes = journey.config?.nodes ?? [];
    const requestedNodes = nodes.filter((n) => requestedNodeIds.includes(n.id));

    const journeyEntrySummary = getEntrySummary(journey);

    return {
        journeyEntrySummary,
        siblingNodeNames: getSiblingNodeNames(journey, requestedNodeIds).slice(
            0,
            MAX_NAMING_SIBLING_NODE_NAMES,
        ),
        requestedNodes: requestedNodes.map((n) => {
            const naming = getNodeNamingSignals(
                n,
                hydratedEntities,
                workspaceTimezone,
            );
            return {
                nodeId: n.id,
                nodeKindTitle: getNodeKindTitle(n),
                existingTitle: n.title,
                existingDescription: isJourneyNodeDescriptionDefault(n)
                    ? ''
                    : n.description,
                primarySignal: naming.primarySignal,
                supportingSignals: naming.supportingSignals,
                criteria: naming.criteria,
            };
        }),
    };
};

const CHANNEL_COUNT_LABELS: Partial<
    Record<CommunicationChannel, { one: string; many: string }>
> = {
    [CommunicationChannel.EMAIL]: { one: 'email', many: 'emails' },
    [CommunicationChannel.SMS]: { one: 'SMS', many: 'SMS' },
    [CommunicationChannel.WHATSAPP]: {
        one: 'WhatsApp message',
        many: 'WhatsApp messages',
    },
    [CommunicationChannel.PUSH]: {
        one: 'push notification',
        many: 'push notifications',
    },
};

const getJourneyCompositionSummary = (
    journey: JourneyCreatePayload,
): JourneyCompositionSummary => {
    const nodes = journey.config?.nodes ?? [];
    const channelCounts = new Map<CommunicationChannel, number>();
    let waitCount = 0;
    let splitCount = 0;

    for (const node of nodes) {
        for (const action of node.actions ?? []) {
            switch (action.type) {
                case ActionType.SEND_MESSAGE: {
                    const { channel } = action.config.messageConfig;
                    if (channel) {
                        channelCounts.set(
                            channel,
                            (channelCounts.get(channel) ?? 0) + 1,
                        );
                    }
                    break;
                }
                case ActionType.WAIT:
                case ActionType.WAIT_UNTIL:
                    waitCount += 1;
                    break;
                case ActionType.SPLIT:
                    splitCount += 1;
                    break;
                default:
                    break;
            }
        }
    }

    const channelMix = Array.from(channelCounts.entries()).map(
        ([channel, count]) => {
            const labels = CHANNEL_COUNT_LABELS[channel];
            if (labels) {
                return `${count} ${count === 1 ? labels.one : labels.many}`;
            }
            const fallback = CHANNEL_LABELS[channel] ?? String(channel);
            return `${count} ${fallback.toLowerCase()}`;
        },
    );

    return {
        nodeCount: nodes.length,
        channelMix,
        waitCount,
        splitCount,
    };
};

export const buildJourneyNameDescriptionContext = (
    journey: JourneyCreatePayload,
    hydratedEntities: JourneyNamingHydratedEntities,
    workspaceTimezone: string = Timezones.UTC,
): JourneyNameDescriptionContext => {
    const nodes = journey.config?.nodes ?? [];
    return {
        existingJourneyName: journey.name ?? '',
        existingJourneyDescription: journey.description,
        journeyEntrySummary: getEntrySummary(journey),
        composition: getJourneyCompositionSummary(journey),
        nodeSignals: nodes
            .slice(0, MAX_JOURNEY_NAMING_NODE_SIGNALS)
            .map((n) => ({
                nodeId: n.id,
                nodeKindTitle: getNodeKindTitle(n),
                actionSignals: getNodeActionSignals(
                    n,
                    hydratedEntities,
                    workspaceTimezone,
                ).slice(0, 8),
                // Placeholder titles are noise the model would otherwise echo back.
                existingTitle: isJourneyNodeTitleDefault(n) ? '' : n.title,
            })),
    };
};
